import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { catchError, filter, forkJoin, interval, of, Subscription, switchMap, take } from "rxjs";
import { DashboardNavigationService } from "src/app/pages/dashboard/services/dashboard-navigation.service";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import { navigateToOnboardingCandidateClaims } from "src/app/shared/components/notifications/notification-registry";
import { TranslationLoaderService } from "src/app/shared/services/translation-loader.service";
import {
  getOnboardingStore,
  INITIAL_SETUP_CANDIDATE_CLAIMS_URL,
  getOnboardingStage,
} from "src/app/core/guards/first-time-user-experience.helper";
import { hasOnboardingPotentialCases } from "./helpers/onboarding-potential-cases.helper";
import { OnboardingModalService } from "./onboarding-modal.service";
import {
  CandidateClaimsSearchCompleteDialogComponent,
  CandidateClaimsSearchCompleteDialogData,
} from "./modals/candidate-claims-search-complete-dialog/candidate-claims-search-complete-dialog.component";

@Injectable({
  providedIn: "root",
})
export class OnboardingCandidateClaimsCompleteService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly translationLoader = inject(TranslationLoaderService);
  private readonly profileService = inject(ProfileService);
  private readonly storeService = inject(StoreService);
  private readonly dashboardNavigation = inject(DashboardNavigationService);
  private readonly onboardingModalService = inject(OnboardingModalService);

  private dialogRef?: MatDialogRef<CandidateClaimsSearchCompleteDialogComponent>;
  private pollSubscription?: Subscription;

  /** Also reachable from candidate-claims-found's "Read while you wait" link. */
  private static readonly KNOWLEDGE_BASE_URL = "/help/knowledge-base";
  /**
   * Safety net for a WS message that never arrives — e.g. a stale Treeslo
   * tab left open from before the store got linked, keeping an old WS
   * connection alive. Same GET the WS event would trigger, just on a timer.
   */
  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncPolling());
  }

  promptNavigateToResults(storeId: string): void {
    if (!storeId || this.dialogRef) {
      return;
    }

    if (this.isOnboardingAlreadyComplete()) {
      this.navigateToClaimManagement(storeId);
      return;
    }

    if (!this.isWatchingForResults()) {
      // Onboarding is still incomplete and the user isn't watching for
      // results right now (e.g. still filling out the store's
      // delivery/threshold form) — do nothing. Once they finish configuring
      // the store, the "continue" step navigates to candidate-claims-found,
      // whose resolver picks up the already-completed results and shows them
      // directly instead of the searching animation.
      return;
    }

    this.translationLoader
      .loadAndMergeViewTranslations("candidate-claims-found")
      .pipe(
        switchMap(() =>
          forkJoin({
            title: this.translate.get(
              "candidate_claims_found.searchCompleteTitle",
            ),
            message: this.translate.get(
              "candidate_claims_found.searchCompleteMessage",
            ),
            okAction: this.translate.get(
              "candidate_claims_found.searchCompleteOk",
            ),
          }),
        ),
      )
      .subscribe((dialogData) => this.openDialog(dialogData, storeId));
  }

  private openDialog(
    dialogData: CandidateClaimsSearchCompleteDialogData,
    storeId: string,
  ): void {
    this.dialogRef = this.dialog.open(
      CandidateClaimsSearchCompleteDialogComponent,
      {
        data: dialogData,
        disableClose: true,
        width: "28rem",
        maxWidth: "95vw",
        panelClass: "onboarding-search-complete-dialog",
      },
    );

    this.dialogRef.afterClosed().subscribe((confirmed) => {
      this.dialogRef = undefined;

      if (!confirmed) {
        return;
      }

      this.navigateAfterPotentialCasesComplete(storeId);
    });
  }

  private navigateAfterPotentialCasesComplete(storeId: string): void {
    if (this.canNavigateToCandidateClaimsFound()) {
      navigateToOnboardingCandidateClaims(storeId, {
        navigate: (routePath, queryParams) => {
          void this.router.navigate([routePath], { queryParams });
        },
      });
      return;
    }

    this.navigateToClaimManagement(storeId);
  }

  private isOnboardingAlreadyComplete(): boolean {
    return !isUserOnboardingInProgress(this.profileService.userProfile());
  }

  /**
   * True while on candidate-claims-found itself, or on the knowledge-base
   * page it links out to while waiting — never on a knowledge-base visit
   * reached some other way (regular help-center browsing isn't "watching").
   */
  private isWatchingForResults(): boolean {
    const url = this.router.url;
    if (url.startsWith(INITIAL_SETUP_CANDIDATE_CLAIMS_URL)) {
      return true;
    }
    if (!url.startsWith(OnboardingCandidateClaimsCompleteService.KNOWLEDGE_BASE_URL)) {
      return false;
    }
    const returnUrl = this.router.parseUrl(url).queryParams["returnUrl"];
    return returnUrl === INITIAL_SETUP_CANDIDATE_CLAIMS_URL;
  }

  private syncPolling(): void {
    if (this.isWatchingForResults() && !this.isOnboardingAlreadyComplete()) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = interval(
      OnboardingCandidateClaimsCompleteService.POLL_INTERVAL_MS,
    ).subscribe(() => this.pollForResults());
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }

  private pollForResults(): void {
    const storeId = getOnboardingStore(this.storeService.stores())?.storeId;
    if (!storeId || this.dialogRef) {
      return;
    }
    this.onboardingModalService
      .getOnboardingPotentialCases(storeId)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        if (hasOnboardingPotentialCases(response)) {
          this.stopPolling();
          this.promptNavigateToResults(storeId);
        }
      });
  }

  /** Same rule {@link preventInitialSetupAccessGuard} applies to candidate-claims-found. */
  private canNavigateToCandidateClaimsFound(): boolean {
    return (
      getOnboardingStage(
        this.profileService.userProfile(),
        this.storeService.stores(),
      ) === "candidate-claims"
    );
  }

  private navigateToClaimManagement(storeId: string): void {
    this.storeService
      .getCachedOrLoad()
      .pipe(take(1))
      .subscribe((stores) => {
        const store = stores.find((item) => item.storeId === storeId);
        this.dashboardNavigation.navigateToClaimsList({
          provider: store?.provider ?? "",
          storeId,
          startDate: "",
          endDate: "",
        });
      });
  }
}
